import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ItemSearchResult } from '../../models/ItemSearchResult';
import { searchItems, ItemSearchSort, ItemSearchPageResponse } from '../../service/SessionService';
import { convertStrDateUTCToStrDateGB } from '../../utils/Utils';
import GroupEditModal from './GroupEditModal';
import GroupEditHistoryTab from './GroupEditHistoryTab';

// ── Status badge ───────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  OK:         'bg-green-100 text-green-700',
  NO:         'bg-red-100 text-red-700',
  OVER:       'bg-yellow-100 text-yellow-700',
  NA:         'bg-gray-100 text-gray-700',
  UNEXPECTED: 'bg-amber-100 text-amber-700',
};
const STATUS_LABELS: Record<string, string> = {
  OK: 'OK', NO: 'Missing', OVER: 'Over', NA: 'N/A', UNEXPECTED: 'Unexpected',
};

const SearchStatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-600'}`}>
    {STATUS_LABELS[status] ?? status}
  </span>
);

// ── Helpers ────────────────────────────────────────────────────────────────

type StatusFilter = 'NOT_OK' | 'PART';

function daysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function isNotOk(status: string): boolean {
  return status === 'NO' || status === 'OVER' || status === 'UNEXPECTED';
}

function normalizePart(pn: string): string {
  return pn.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
}

function partsCompatible(a: string, b: string): boolean {
  const na = normalizePart(a);
  const nb = normalizePart(b);
  return na === nb || na.includes(nb) || nb.includes(na);
}

// ── Loading skeleton ───────────────────────────────────────────────────────

const TableSkeleton: React.FC = () => (
  <div className="space-y-2 py-2">
    {Array.from({ length: 8 }).map((_, i) => (
      <div key={i} className="h-9 bg-gray-100 rounded animate-pulse" style={{ opacity: 1 - i * 0.08 }} />
    ))}
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────

type Tab = 'search' | 'history';

// Active filter snapshot — committed when Apply is clicked.
// Changing any field in this ref triggers a feed reset.
interface ActiveFilters {
  statusFilter: StatusFilter;
  partNumber: string;
  fromDate: string;
  toDate: string;
  sort: ItemSearchSort;
}

const ItemSearchPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialTab        = searchParams.get('tab') === 'history' ? 'history' : 'search';
  const expandGroupEditId = searchParams.get('expand') ?? undefined;

  const [activeTab, setActiveTab] = useState<Tab>(initialTab as Tab);

  // ── Filter form state (pending, not yet applied) ─────────────────────
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('NOT_OK');
  const [partNumber, setPartNumber]     = useState('');
  const [fromDate, setFromDate]         = useState(daysAgo(3));
  const [toDate, setToDate]             = useState(today());
  const [sort, setSort]                 = useState<ItemSearchSort>('PART_ASC');

  // ── Infinite scroll state ────────────────────────────────────────────
  const [allResults, setAllResults]     = useState<ItemSearchResult[]>([]);
  const [currentPage, setCurrentPage]   = useState(0);
  const [hasMore, setHasMore]           = useState(false);
  const [totalCount, setTotalCount]     = useState(0);
  const [loading, setLoading]           = useState(false);
  const [loadingMore, setLoadingMore]   = useState(false);
  const [searched, setSearched]         = useState(false);
  const [error, setError]               = useState<string | null>(null);

  // Tracks the filters that produced the current feed — used to fetch next pages correctly
  const activeFiltersRef = useRef<ActiveFilters>({
    statusFilter: 'NOT_OK', partNumber: '', fromDate: daysAgo(3), toDate: today(), sort: 'PART_ASC',
  });

  // ── Group edit selection ─────────────────────────────────────────────
  const [selectedIds, setSelectedIds]               = useState<Set<string>>(new Set());
  const [selectionError, setSelectionError]         = useState<string | null>(null);
  const [showGroupEditModal, setShowGroupEditModal] = useState(false);

  // ── Refs ─────────────────────────────────────────────────────────────
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const sentinelRef        = useRef<HTMLDivElement>(null);

  // ── Derived ──────────────────────────────────────────────────────────
  const hasPartNumber      = partNumber.trim().length >= 2;
  const firstSelectedItem  = allResults.find(r => selectedIds.has(r.itemId));
  const selectedItems      = allResults.filter(r => selectedIds.has(r.itemId));

  // Revert "All" status if part number is cleared
  useEffect(() => {
    if (statusFilter === 'PART' && !hasPartNumber) setStatusFilter('NOT_OK');
  }, [hasPartNumber, statusFilter]);

  // ── Core fetch function ───────────────────────────────────────────────
  const fetchPage = useCallback(async (filters: ActiveFilters, page: number, append: boolean) => {
    if (filters.statusFilter === 'PART' && filters.partNumber.trim().length < 2) {
      setError('Please enter at least 2 characters for the part number when using "All" status.');
      return;
    }

    if (append) setLoadingMore(true);
    else { setLoading(true); setSearched(false); }
    setError(null);

    try {
      const res: ItemSearchPageResponse = await searchItems({
        statusFilter: filters.statusFilter === 'NOT_OK'
          ? (filters.partNumber.trim() ? 'NOT_OK_PART' : 'NOT_OK')
          : 'PART',
        partNumber: filters.partNumber.trim() || undefined,
        fromDate:   filters.fromDate || undefined,
        toDate:     filters.toDate   || undefined,
        sort:       filters.sort,
        page,
        size: 20,
      });

      setAllResults(prev => append ? [...prev, ...res.content] : res.content);
      setCurrentPage(page);
      setHasMore(res.hasMore);
      setTotalCount(res.totalCount);
      setSearched(true);
    } catch {
      setError('Search failed. Please try again.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  // ── Initial auto-load on mount ────────────────────────────────────────
  useEffect(() => {
    const filters = activeFiltersRef.current;
    fetchPage(filters, 0, false);
  }, []); // eslint-disable-line

  // ── IntersectionObserver on sentinel inside scroll container ─────────
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loading && !loadingMore) {
          fetchPage(activeFiltersRef.current, currentPage + 1, true);
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '150px',
      }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, currentPage, fetchPage]);

  // ── Apply filters — reset feed, keep selection ────────────────────────
  const handleSearch = () => {
    const filters: ActiveFilters = { statusFilter, partNumber, fromDate, toDate, sort };
    activeFiltersRef.current = filters;
    // Reset feed (selection kept intentionally per spec)
    setAllResults([]);
    setCurrentPage(0);
    setHasMore(false);
    setSelectionError(null);
    fetchPage(filters, 0, false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const setPreset = (days: number) => {
    const fd = daysAgo(days);
    const td = today();
    setFromDate(fd);
    setToDate(td);
    const filters: ActiveFilters = { statusFilter, partNumber, fromDate: fd, toDate: td, sort };
    activeFiltersRef.current = filters;
    setAllResults([]);
    setCurrentPage(0);
    setHasMore(false);
    setSelectionError(null);
    fetchPage(filters, 0, false);
  };

  // ── Selection with part-number compatibility ──────────────────────────
  const toggleSelect = (item: ItemSearchResult) => {
    setSelectionError(null);
    if (selectedIds.has(item.itemId)) {
      setSelectedIds(prev => { const n = new Set(prev); n.delete(item.itemId); return n; });
      return;
    }
    if (firstSelectedItem && !partsCompatible(firstSelectedItem.partNumber, item.partNumber)) {
      setSelectionError(
        `Part number mismatch: "${item.partNumber}" is not compatible with "${firstSelectedItem.partNumber}". ` +
        `Only items with matching part numbers can be group-edited together.`
      );
      return;
    }
    setSelectedIds(prev => { const n = new Set(prev); n.add(item.itemId); return n; });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectionError(null);
  };

  const handleGroupEditDone = async () => {
    setShowGroupEditModal(false);
    clearSelection();
    // Reload first page
    const filters = activeFiltersRef.current;
    setAllResults([]);
    setCurrentPage(0);
    fetchPage(filters, 0, false);
  };

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <section className="dashboard-section space-y-6">

      {/* Tab switcher */}
      <div className="bg-white rounded-2xl shadow-sm">
        <div className="flex border-b px-6">
          {(['search', 'history'] as Tab[]).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition ${
                activeTab === tab ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              <i className={`fas ${tab === 'search' ? 'fa-search' : 'fa-history'} mr-2`}></i>
              {tab === 'search' ? 'Item Search' : 'Group Edit History'}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'history' && <GroupEditHistoryTab expandGroupEditId={expandGroupEditId} />}

      {activeTab === 'search' && (
        <>
          {/* Search card */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Item Search</h2>

            <div className="flex flex-wrap gap-4 items-end">
              {/* Part number */}
              <div className="flex-1 min-w-48">
                <label className="block text-xs font-medium text-gray-600 mb-1">Part Number</label>
                <input type="text"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  placeholder="Enter part number…"
                  value={partNumber} onChange={e => setPartNumber(e.target.value)} onKeyDown={handleKeyDown} />
              </div>

              {/* Status */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
                <select
                  className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                  value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value as StatusFilter)}
                >
                  <option value="NOT_OK">Missing + Over</option>
                  <option value="PART" disabled={!hasPartNumber}>
                    All{!hasPartNumber ? ' (enter part number first)' : ''}
                  </option>
                </select>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Sort By</label>
                <select
                  className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white"
                  value={sort}
                  onChange={e => setSort(e.target.value as ItemSearchSort)}
                >
                  <option value="PART_ASC">Part Number (A → Z)</option>
                  <option value="DATE_DESC">Date (newest first)</option>
                  <option value="DATE_ASC">Date (oldest first)</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
                <input type="date"
                  className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  value={fromDate} onChange={e => setFromDate(e.target.value)} />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
                <input type="date"
                  className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                  value={toDate} onChange={e => setToDate(e.target.value)} />
              </div>

              <button onClick={handleSearch} disabled={loading}
                className="gradient-bg text-white px-5 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition disabled:opacity-60">
                {loading
                  ? <span><i className="fas fa-circle-notch fa-spin mr-1"></i>Searching…</span>
                  : 'Search'}
              </button>
            </div>

            {/* Quick presets */}
            <div className="flex gap-2 mt-3">
              <span className="text-xs text-gray-500 self-center">Quick select:</span>
              {[3, 7, 30, 90].map(d => (
                <button key={d} onClick={() => setPreset(d)}
                  className="text-xs px-2 py-1 border rounded-lg text-gray-600 hover:bg-gray-50 transition">
                  Last {d} days
                </button>
              ))}
              <button onClick={() => { setFromDate(''); setToDate(''); }}
                className="text-xs px-2 py-1 border rounded-lg text-gray-600 hover:bg-gray-50 transition">
                All time
              </button>
            </div>

            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          </div>

          {/* Floating selection toolbar — sticky so it stays visible while scrolling */}
          {selectedIds.size > 0 && (
            <div className="sticky top-2 z-20 bg-blue-50 border border-blue-200 rounded-2xl p-4 flex flex-wrap items-center gap-4 shadow-md">
              <div className="flex-1 text-sm text-blue-800">
                <i className="fas fa-edit mr-2"></i>
                <strong>{selectedIds.size}</strong> item{selectedIds.size !== 1 ? 's' : ''} selected
                {firstSelectedItem && (
                  <span className="ml-2 text-blue-600 font-mono text-xs">· {firstSelectedItem.partNumber}</span>
                )}
              </div>
              {selectionError && (
                <p className="w-full text-xs text-orange-700">
                  <i className="fas fa-exclamation-triangle mr-1"></i>{selectionError}
                </p>
              )}
              <button onClick={() => setShowGroupEditModal(true)}
                className="gradient-bg text-white px-4 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition">
                <i className="fas fa-layer-group mr-1"></i>
                Edit Together ({selectedIds.size})
              </button>
              <button onClick={clearSelection}
                className="text-sm text-blue-600 hover:text-blue-800 border border-blue-300 rounded-lg px-3 py-2 hover:bg-blue-100 transition">
                <i className="fas fa-times mr-1"></i>Clear selection
              </button>
            </div>
          )}

          {/* Results card */}
          {(searched || loading) && (
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-700">
                  {loading && !loadingMore
                    ? 'Searching…'
                    : totalCount === 0
                    ? 'No results found'
                    : (
                      <>
                        Showing <strong>{allResults.length}</strong>
                        {hasMore && <> of <strong>{totalCount}</strong></>} result{totalCount !== 1 ? 's' : ''}
                        {!hasMore && allResults.length > 0 && <span className="text-gray-400"> (all loaded)</span>}
                      </>
                    )}
                </h3>
                {!loading && allResults.length > 0 && (
                  <p className="text-xs text-gray-400">
                    Closed sessions only · select Not OK items to edit together
                  </p>
                )}
              </div>

              {/* Loading skeleton (first page only) */}
              {loading && !loadingMore && <TableSkeleton />}

              {!loading && allResults.length > 0 && (
                /* Scroll container — sentinel lives inside here */
                <div
                  ref={scrollContainerRef}
                  className="overflow-auto rounded-lg border border-gray-200"
                  style={{ maxHeight: 'calc(100vh - 340px)' }}
                >
                  <table className="min-w-full text-sm">
                    <thead className="sticky top-0 z-10 bg-white shadow-sm">
                      <tr className="border-b text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        <th className="px-3 py-2 bg-white">Select</th>
                        <th className="px-3 py-2 bg-white">Part Number</th>
                        <th className="px-3 py-2 bg-white">Description</th>
                        <th className="px-3 py-2 bg-white">Delivery Note</th>
                        <th className="px-3 py-2 bg-white">Order Type</th>
                        <th className="px-3 py-2 bg-white">Session</th>
                        <th className="px-3 py-2 bg-white">Date</th>
                        <th className="px-3 py-2 bg-white text-right">Expected</th>
                        <th className="px-3 py-2 bg-white text-right">Received</th>
                        <th className="px-3 py-2 bg-white">Status</th>
                        <th className="px-3 py-2 bg-white">Reconciled</th>
                        <th className="px-3 py-2 bg-white">Last Update</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allResults.map(row => {
                        const isNotOkRow = isNotOk(row.status);
                        const isSelected = selectedIds.has(row.itemId);
                        const canSelect  = isNotOkRow && row.sessionClosed && !row.sessionPosted;
                        const isIncompat = !isSelected && firstSelectedItem != null && canSelect &&
                                           !partsCompatible(firstSelectedItem.partNumber, row.partNumber);

                        const rowBg = isSelected
                          ? 'bg-blue-50'
                          : row.status === 'NO' ? 'bg-red-50/40'
                          : (row.status === 'UNEXPECTED' || row.status === 'OVER') ? 'bg-amber-50/40'
                          : '';

                        return (
                          <tr key={row.itemId}
                            className={`border-b hover:bg-gray-50 transition ${rowBg} ${isIncompat ? 'opacity-40' : ''}`}>
                            <td className="px-3 py-2">
                              {canSelect && (
                                <input type="checkbox" checked={isSelected}
                                  onChange={() => toggleSelect(row)}
                                  className="accent-blue-500"
                                  disabled={isIncompat}
                                  title={isIncompat
                                    ? 'Part number not compatible with current selection'
                                    : 'Select for group edit'}
                                />
                              )}
                              {isNotOkRow && (!row.sessionClosed || row.sessionPosted) && (
                                <span className="text-xs text-gray-400"
                                  title={row.sessionPosted
                                    ? 'Session is posted — no edits allowed'
                                    : 'Session must be closed before editing'}>
                                  <i className="fas fa-lock"></i>
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">{row.partNumber}</td>
                            <td className="px-3 py-2 max-w-xs truncate" title={row.description}>{row.description}</td>
                            <td className="px-3 py-2 text-xs text-gray-600 max-w-xs truncate"
                                title={row.deliveryNotes?.join(', ')}>
                              {row.deliveryNotes?.length ? row.deliveryNotes.join(', ') : '—'}
                            </td>
                            <td className="px-3 py-2 text-center">{row.orderType ?? '—'}</td>
                            <td className="px-3 py-2">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs ${
                                row.sessionPosted ? 'bg-slate-100 text-slate-600'
                                  : row.sessionClosed ? 'bg-green-100 text-green-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {row.sessionTitle}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                              {row.sessionDate ? convertStrDateUTCToStrDateGB(row.sessionDate) : '—'}
                            </td>
                            <td className="px-3 py-2 text-right">{row.shipQtd}</td>
                            <td className="px-3 py-2 text-right">
                              {row.sessionClosed ? row.quantity : (
                                <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700"
                                  title="Scanning in progress — quantity committed when session closes">Live</span>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              <SearchStatusBadge status={row.status} />
                            </td>
                            <td className="px-3 py-2 text-center">
                              {row.reconcileRefCount > 0 ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700"
                                  title={`Involved in ${row.reconcileRefCount} group edit(s)`}>
                                  <i className="fas fa-exchange-alt"></i>{row.reconcileRefCount}
                                </span>
                              ) : (
                                <span className="text-gray-300 text-xs">—</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-400 whitespace-nowrap">
                              {row.lastUpdate ? convertStrDateUTCToStrDateGB(row.lastUpdate) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Sentinel — inside the scroll container so IntersectionObserver fires on container scroll */}
                  <div ref={sentinelRef} className="h-4" />

                  {/* Inline loading-more indicator */}
                  {loadingMore && (
                    <div className="flex items-center justify-center py-3 text-gray-400 text-sm border-t">
                      <i className="fas fa-circle-notch fa-spin mr-2"></i>Loading more…
                    </div>
                  )}

                  {/* End-of-feed indicator */}
                  {!hasMore && !loadingMore && allResults.length > 0 && (
                    <div className="text-center text-xs text-gray-400 py-3 border-t">
                      All {totalCount} result{totalCount !== 1 ? 's' : ''} loaded
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Group edit modal */}
      {showGroupEditModal && selectedItems.length > 0 && (
        <GroupEditModal
          items={selectedItems}
          onClose={() => setShowGroupEditModal(false)}
          onSaved={handleGroupEditDone}
        />
      )}
    </section>
  );
};

export default ItemSearchPage;
